import { useMemo } from 'react'

import { ICON_MAP } from '@/components/componentLibrary/Icon/Icons'
import { PageItem } from '@/components/layouts/CurbsNav/types'
import {
  useCurbActivityAccess,
  useCurbCitationsAccess,
  useCurbFeesAndRevenueAccess,
  useCurbGeographiesMapAccess,
  useCurbMetricsAccess,
  useCurbOccupancyAccess,
  useCurbParkingDashboardAccess,
  useCurbParkingInsightsAccess,
  useCurbParkingTransactionsAccess,
  useCurbPricingEngineAccess,
  useCurbRegulationsAccess,
  useCurbRegulationsLibraryAccess,
} from '@/modules/permissions/pageAccessHooks'
import { useCurrentProduct, useCurrentRegion } from '@/modules/urlRouting/hooks'
import { PATHS, SEGMENTS } from '@/modules/urlRouting/paths'
import { generatePathWithRegionId } from '@/modules/urlRouting/utils'

/**
 * Nav items for the curb products: Elevate, Digital Smart Zones and
 * Regulations. All three have a flat list of pages.
 */
export const useCurbPages = (): PageItem[] => {
  const {
    data: { regionId },
  } = useCurrentRegion()
  const currentProduct = useCurrentProduct()

  const hasCurbDashboardAccess = useCurbParkingDashboardAccess()
  const hasCurbCitationsAccess = useCurbCitationsAccess()
  const hasCurbFeesAndRevenueAccess = useCurbFeesAndRevenueAccess()
  const hasCurbMetricsAccess = useCurbMetricsAccess()
  const hasCurbActivityAccess = useCurbActivityAccess()
  const hasCurbParkingInsightsAccess = useCurbParkingInsightsAccess()
  const hasCurbPricingEngineAccess = useCurbPricingEngineAccess()
  const hasCurbParkingTransactionsAccess = useCurbParkingTransactionsAccess()
  const hasCurbOccupancyAccess = useCurbOccupancyAccess()
  const hasCurbRegulationAccess = useCurbRegulationsAccess()
  const hasCurbRegulationsLibraryAccess = useCurbRegulationsLibraryAccess()
  const hasCurbGeographiesAccess = useCurbGeographiesMapAccess()

  const geographiesPath = useMemo(() => {
    switch (currentProduct) {
      case SEGMENTS.MANAGEMENT:
        return PATHS.MANAGEMENT.GEOGRAPHIES
      case SEGMENTS.ELEVATE:
        return PATHS.ELEVATE.GEOGRAPHIES
      case SEGMENTS.REGULATIONS:
        return PATHS.REGULATIONS.GEOGRAPHIES
      default:
        return ''
    }
  }, [currentProduct])

  return useMemo((): PageItem[] => {
    const pages: PageItem[] = []
    switch (currentProduct) {
      case SEGMENTS.MANAGEMENT:
        pages.push(
          {
            title: 'Dashboard Metrics',
            link: generatePathWithRegionId(PATHS.MANAGEMENT.METRICS, regionId),
            disabled: !hasCurbMetricsAccess,
            icon: <ICON_MAP.shipping />,
          },
          {
            title: 'Activity Map',
            link: generatePathWithRegionId(PATHS.MANAGEMENT.ACTIVITY, regionId),
            disabled: !hasCurbActivityAccess,
            icon: <ICON_MAP.pin />,
          },
          {
            title: 'Fees and Revenue',
            link: generatePathWithRegionId(PATHS.MANAGEMENT.FEES_AND_REVENUE, regionId),
            disabled: !hasCurbFeesAndRevenueAccess,
            icon: <ICON_MAP.money />,
          }
        )
        break
      case SEGMENTS.ELEVATE:
        pages.push(
          {
            title: 'Dashboard',
            link: generatePathWithRegionId(PATHS.ELEVATE.DASHBOARD, regionId),
            disabled: !hasCurbDashboardAccess,
            icon: <ICON_MAP.dashboard />,
          },
          {
            title: 'Parking Revenue',
            link: generatePathWithRegionId(PATHS.ELEVATE.REVENUE, regionId),
            disabled: !hasCurbParkingInsightsAccess,
            icon: <ICON_MAP.money />,
          },
          {
            title: 'Parking Sessions',
            link: generatePathWithRegionId(PATHS.ELEVATE.SESSIONS, regionId),
            disabled: !hasCurbParkingTransactionsAccess,
            icon: <ICON_MAP.list />,
          },
          {
            title: 'Occupancy',
            link: generatePathWithRegionId(PATHS.ELEVATE.OCCUPANCY, regionId),
            disabled: !hasCurbOccupancyAccess,
            icon: <ICON_MAP.pin />,
          },
          {
            title: 'Pricing Engine',
            link: generatePathWithRegionId(PATHS.ELEVATE.PRICING_ENGINE, regionId),
            disabled: !hasCurbPricingEngineAccess,
            icon: <ICON_MAP.autoAwesome />,
          },
          {
            title: 'Enforcement',
            link: generatePathWithRegionId(PATHS.ELEVATE.ENFORCEMENT, regionId),
            disabled: !hasCurbCitationsAccess,
            icon: <ICON_MAP.localPolice />,
          }
        )
        break
      case SEGMENTS.REGULATIONS:
        pages.push(
          {
            title: 'Regulations Library',
            link: generatePathWithRegionId(PATHS.REGULATIONS.LIBRARY, regionId),
            disabled: !hasCurbRegulationsLibraryAccess,
            icon: <ICON_MAP.policy />,
          },
          {
            title: 'Regulations Map',
            link: generatePathWithRegionId(PATHS.REGULATIONS.MAP, regionId),
            disabled: !hasCurbRegulationAccess,
            icon: <ICON_MAP.pin />,
          }
        )
        break
    }

    if (geographiesPath)
      pages.push({
        title: 'Geographies Map',
        link: generatePathWithRegionId(geographiesPath, regionId),
        disabled: !hasCurbGeographiesAccess,
        icon: <ICON_MAP.landscape />,
      })

    return pages
  }, [
    currentProduct,
    regionId,
    hasCurbDashboardAccess,
    hasCurbCitationsAccess,
    hasCurbMetricsAccess,
    hasCurbActivityAccess,
    hasCurbFeesAndRevenueAccess,
    hasCurbParkingInsightsAccess,
    hasCurbParkingTransactionsAccess,
    hasCurbPricingEngineAccess,
    hasCurbOccupancyAccess,
    hasCurbRegulationAccess,
    hasCurbRegulationsLibraryAccess,
    hasCurbGeographiesAccess,
    geographiesPath,
  ])
}
